import type { ValidationRule } from '@elements/form-validation/dist/core/index.js'

// Same semantics as the HTML `accept` attribute (the lib maps `accept` onto this
// rule): ".ext"/"ext" match the extension, "type/*" a MIME prefix, "type/sub" an
// exact MIME type. Every selected file must match at least one entry.
const matchesAccept = (file: { name: string; type?: string }, entries: string[]): boolean => {
    const extension = file.name.includes('.') ? file.name.split('.').pop()!.toLowerCase() : ''
    const mime = (file.type ?? '').toLowerCase()
    return entries.some(entry => {
        if (entry.endsWith('/*')) return mime !== '' && mime.startsWith(entry.slice(0, -1))
        if (entry.includes('/')) return mime !== '' && mime === entry
        return extension !== '' && extension === entry.replace(/^\./, '')
    })
}

const file: ValidationRule = {
    validate: (value, param, field) => {
        if (!value || !param) return true
        const entries = param
            .split(',')
            .map(entry => entry.trim().toLowerCase())
            .filter(entry => entry !== '')
        if (entries.length === 0) return true
        const files = (field as HTMLInputElement | undefined)?.files
        const selected = files && files.length > 0
            ? Array.from(files)
            : [{ name: value.split(/[\\/]/).pop() ?? value, type: '' }]
        return selected.every(f => matchesAccept(f, entries))
    },
}

const maxFiles: ValidationRule = {
    validate: (value, param, field) => {
        if (!value || !param) return true
        const files = (field as HTMLInputElement | undefined)?.files
        if (!files) return true
        const limit = Number(param)
        if (Number.isNaN(limit)) return true
        return files.length <= limit
    },
}

const maxFileSize: ValidationRule = {
    validate: (value, param, field) => {
        if (!value || !param) return true
        const files = (field as HTMLInputElement | undefined)?.files
        if (!files) return true
        const maxKb = Number(param)
        if (Number.isNaN(maxKb)) return true
        const maxBytes = maxKb * 1024
        return Array.from(files).every(f => f.size <= maxBytes)
    },
}

const fieldsetMinCount: ValidationRule = {
    validate: (value: string, param?: string) => {
        const count = parseInt(value, 10);
        const min = parseInt(param ?? '1', 10);
        return !Number.isFinite(min) || !Number.isFinite(count) || count >= min;
    },
}

export const customValidators: Record<string, ValidationRule> = {
    file,
    maxFiles,
    maxFileSize,
    fieldsetMinCount,
}
